import { melbourneToday } from '../../shared/dates';
import { LEAD_SOURCE_LABELS, LEAD_STATUS_LABELS, LEAD_TYPE_LABELS, PHASE_LABELS } from '../../shared/types';
import { downloadCsv } from '../lib/csvDownload';
import { useId, useRef, useState } from 'react';
import { reportError } from '../lib/errorEvents';
import type { PageMenuItem } from './PageMenu';

type Params = Record<string, string | number | boolean | null | undefined>;
export function csvExportUrl(module: string, params: Params): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') query.set(key, typeof value === 'boolean' ? value ? '1' : '0' : String(value));
  }
  return `/api/admin/export/${module}.csv${query.size ? `?${query}` : ''}`;
}
export function DownloadIcon() {
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3v12m-4-4 4 4 4-4M5 18v3h14v-3" /></svg>;
}
type ExportOptions = {
  count: number;
  /** Labels from page-owned pickers, especially related-record names. */
  filterLabels?: Record<string, string | undefined>;
  projectNumber?: number | null;
};
const humanize = (value: string) => value.replace(/[_-]/g, ' ').replace(/^./, c => c.toUpperCase());
export function csvExportDetails(module: string, params: Params, options: ExportOptions, busy = false) {
  const labels: Record<string, Record<string, string>> = {
    view: { week: 'This week' },
    status: module === 'clients' ? { lead: 'Lead', active: 'Active', past: 'Past', archived: 'Archived' } : LEAD_STATUS_LABELS,
    phase: PHASE_LABELS, source: LEAD_SOURCE_LABELS, lead_type: LEAD_TYPE_LABELS,
    priority: { high: 'High priority' }, brand: { ss: 'SS', hsp: 'HSP' },
    showVoided: { true: 'Include voided' }, project: { none: 'No project' },
  };
  const filters = [...new Set(Object.entries(params).flatMap(([key, value]) => {
    if (value == null || value === '' || value === false || ['sort', 'recent', 'project_id'].includes(key)
      || (key === 'view' && ['invoices', 'outreach'].includes(String(value)))) return [];
    if (options.filterLabels?.[key]) return [options.filterLabels[key]!];
    const text = String(value);
    if (key === 'query' || key === 'q') return text.trim() ? [`“${text.trim()}”`] : [];
    if (['client', 'project', 'archive', 'video_type', 'fy', 'run_date'].includes(key)) return [labels[key]?.[text] ?? text];
    return text.split(',').map(v => labels[key]?.[v] ?? humanize(v));
  }))];
  const fy = String(params.fy ?? '');
  const filename = module === 'bas' ? `bas-${(/^FY\d{2}-\d{2}$/.test(fy) ? `FY${fy.slice(-2)}` : fy).toLowerCase()}.csv`
    : `${module}${module === 'deliverables' && options.projectNumber != null ? `-${options.projectNumber}` : ''}-${melbourneToday()}.csv`;
  const noun = module === 'bas' ? 'quarters' : module === 'outreach' ? 'outreach leads' : module;
  const summary = `${options.count} ${noun}`;
  const money = module === 'bas' ? ' · money in dollars' : ['projects', 'invoices', 'leads'].includes(module) ? ' · money in dollars + cents' : '';
  return { filters, filename, hint: `Exports the ${summary} shown`, fileHint: `${filename} · Melbourne dates${money}`,
    sublabel: busy ? `Preparing ${filename}…` : `${summary}${filters.length ? ` · ${filters.join(', ')}` : ''}` };
}
export function useCsvExport(module: string, params: Params, options: ExportOptions) {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const href = csvExportUrl(module, params);
  const details = csvExportDetails(module, params, options, busy);
  async function download() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await downloadCsv(href, details.filename);
    } catch (error) { reportError(error); }
    finally { inFlight.current = false; setBusy(false); }
  }
  const icon = busy ? <span className="csv-spin" /> : <DownloadIcon />;
  const label = busy ? 'Exporting…' : 'Export CSV';
  const item: PageMenuItem = { key: `export-${module}`, className: 'csv-export-menu', icon, label, sublabel: details.sublabel, disabled: busy,
    keepOpen: true, onSelect: () => void download() };
  return { busy, href, download, icon, label, ...details, item };
}
export type CsvExportState = ReturnType<typeof useCsvExport>;
export function CsvExportButton({ state, quiet = false, above = false }: { state: CsvExportState; quiet?: boolean; above?: boolean }) {
  const hintId = useId();
  return <span className={`export-anchor${above ? ' export-above' : ''}`}>
    <button type="button" className={`${quiet ? 'group-link' : 'btn'} export${state.busy ? ' loading' : ''}`}
      disabled={state.busy} aria-busy={state.busy} aria-describedby={hintId} onClick={() => void state.download()}>
      {state.icon}{state.label}
    </button>
    <span className="hint-pop csv-hint" id={hintId} role="tooltip"><b>{state.hint}</b>{state.filters.length > 0 && <>, honouring the current filters:
      <span className="chips">{state.filters.map(filter => <span key={filter}>{filter}</span>)}</span></>}
      <span className="file">{state.fileHint}</span>
    </span>
  </span>;
}
